// Shared by the ZATCA master pages: option lists, labels, API paths and the
// shape rules ZATCA applies. The rules are checked here so a mistake is caught
// on the form rather than as an unexplained rejection from ZATCA later. The
// backend must check them again — the browser is not the last line.

export const ZATCA_ENVIRONMENT_OPTIONS = [
    { label: 'Sandbox (developer portal)', value: 'Sandbox' },
    { label: 'Simulation', value: 'Simulation' },
    { label: 'Production', value: 'Production' }
];

export const ZATCA_INVOICE_TYPE_OPTIONS = [
    { label: 'Standard and simplified (B2B and B2C)', value: '1100' },
    { label: 'Standard only (B2B)', value: '1000' },
    { label: 'Simplified only (B2C)', value: '0100' }
];

export const ZATCA_STATUS = {
    NotOnboarded: { label: 'Not onboarded', className: 'p-button-secondary' },
    ComplianceIssued: { label: 'Compliance checks', className: 'p-button-warning' },
    Ready: { label: 'Ready', className: 'p-button-success' },
    Revoked: { label: 'Revoked', className: 'p-button-danger' }
};

/** Name of the unit list call. Also the key the generic save and delete use to refresh it. */
export const zatcaUnitListFunction = (companyId) => 'zatca/GetZatcaEgsUnitList?companyId=' + companyId;

/**
 * The document types a unit must pass compliance checks for, from its
 * invoice-type flags: position 1 is standard, position 2 simplified.
 */
export const requiredComplianceKinds = (invoiceTypeFlags) => {
    const flags = invoiceTypeFlags || '';
    const kinds = [];
    if (flags.charAt(1) === '1') kinds.push('Simplified');
    if (flags.charAt(0) === '1') kinds.push('Standard');
    return kinds;
};

export const invoiceTypeLabel = (flags) => {
    const option = ZATCA_INVOICE_TYPE_OPTIONS.find(item => item.value === flags);
    return option ? option.label : (flags || '—');
};

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

export const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

/** The message to show for a failed call: the API's own error text when it sent one. */
export const apiErrorMessage = (err, fallback) => {
    const data = err && err.response && err.response.data;
    if (typeof data === 'string' && data.trim() !== '') return data;
    if (data && (data.error || data.message)) return data.error || data.message;
    return fallback;
};

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

const hasDigits = (value, count) => new RegExp('^\\d{' + count + '}$').test(String(value || '').trim());

// Byte length as UTF-8. The seller name is written into the invoice QR code,
// whose length field holds at most 255 bytes, and Arabic takes two per letter.
const utf8Length = (value) => encodeURIComponent(String(value || '')).replace(/%[A-F\d]{2}/g, 'x').length;

export const isVatNumber = (value) => /^3\d{13}3$/.test(String(value || '').trim());

/** Messages for everything wrong with the company details; empty when valid. */
export const validateZatcaCompany = (company) => {
    const errors = [];
    if (isBlank(company.nameAr)) {
        errors.push('Legal name (Arabic) is required');
    } else if (utf8Length(company.nameAr.trim()) > 255) {
        errors.push('Legal name is too long for the invoice QR code; shorten it to the registered name');
    }
    if (!isVatNumber(company.vatNo)) errors.push('VAT number must be 15 digits, starting and ending with 3');
    if (isBlank(company.street)) errors.push('Street is required');
    if (!hasDigits(company.buildingNo, 4)) errors.push('Building number must be exactly 4 digits');
    if (!hasDigits(company.additionalNo, 4)) errors.push('Additional number must be exactly 4 digits');
    if (isBlank(company.district)) errors.push('District is required');
    if (isBlank(company.city)) errors.push('City is required');
    if (!hasDigits(company.postalCode, 5)) errors.push('Postal code must be exactly 5 digits');
    return errors;
};

/** Messages for everything wrong with a unit; empty when valid. */
export const validateZatcaUnit = (unit) => {
    const errors = [];
    if (isBlank(unit.branchId)) errors.push('Branch is required');
    if (isBlank(unit.commonName)) errors.push('Unit name is required');
    if (isBlank(unit.organizationUnit)) errors.push('Organization unit is required');
    if (isBlank(unit.location)) errors.push('Location is required');
    if (!/^\d+$/.test(String(unit.crNumber || '').trim())) errors.push('CR number is required and must contain digits only');
    if (isBlank(unit.invoiceTypeFlags)) errors.push('Invoice types are required');
    if (isBlank(unit.industry)) errors.push('Industry is required');
    if (isBlank(unit.environment)) errors.push('Environment is required');
    return errors;
};
